import React from "react";
import { Link } from "react-router-dom";
import { ColumnDef, Row, createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { LoadboardManifestShipment, LoadboardManifestShipmentTerminal } from "app/data/load-board/models";

const columnHelper = createColumnHelper<LoadboardManifestShipment>();

export const MANIFEST_SHIPMENTS_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "expand", label: "Expand" },
  { id: "probillNumber", label: "Probill" },
  { id: "status", label: "Status" },
  { id: "origin", label: "Origin" },
  { id: "destination", label: "Destination" },
  { id: "etaDate", label: "ETA" },
  { id: "rollCount", label: "Rolls" },
  { id: "palletCount", label: "Pallets" },
  { id: "huCount", label: "HU" },
  { id: "totalWeight", label: "Weight" },
  { id: "poNumber", label: "PO" },
  { id: "bolNumber", label: "BOL" },
  { id: "billDate", label: "Bill Date" },
  { id: "rate", label: "Rate" },
  { id: "podDate", label: "POD Date" },
];

export const MANIFEST_SHIPMENTS_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [
  "poNumber",
  "bolNumber",
  "billDate",
  "rate",
  "podDate",
];

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MM/DD/YY") : String(value);
};

const formatNumber = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en", { useGrouping: true, maximumFractionDigits: 0 });
};

const formatCurrency = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const isPresent = (value: unknown): value is string | number => {
  if (value == null) return false;
  const s = String(value).trim();
  return s !== "" && s.toLowerCase() !== "null";
};

/** e.g. "ANA Anaheim 109" */
export const formatManifestShipmentTerminal = (
  terminal?: LoadboardManifestShipmentTerminal | null
): string => {
  if (!terminal) return "";
  const airportCode = isPresent(terminal.airportCode) ? String(terminal.airportCode).trim() : "";
  const terminalName = isPresent(terminal.terminalName) ? String(terminal.terminalName).trim() : "";
  const terminalNumber = isPresent(terminal.terminalNumber) ? String(terminal.terminalNumber).trim() : "";
  return [airportCode, terminalName, terminalNumber].filter(Boolean).join(" - ");
};

const getStatusColor = (title?: string): TagColor => {
  const t = (title ?? "").toUpperCase();
  if (t.includes("DELIVER") || t.includes("COMPLETE") || t.includes("ARRIVED")) return TagColor.GREEN;
  if (t.includes("TRANSIT") || t.includes("INBOUND") || t.includes("OUTBOUND")) return TagColor.BLUE;
  if (t.includes("HOLD") || t.includes("EXCEPT") || t.includes("LATE")) return TagColor.RED;
  if (t.includes("LOAD") || t.includes("DOCK")) return TagColor.PURPLE;
  return TagColor.GREY;
};

export const getManifestShipmentsColumns = (): ColumnDef<LoadboardManifestShipment, any>[] => [
  columnHelper.display({
    id: "expand",
    header: "",
    size: 40,
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    enableResizing: false,
    cell: ({ row }) => {
      if (!row.getCanExpand()) return null;
      return (
        <button
          type="button"
          className="manifest-shipments-expand-btn"
          onClick={row.getToggleExpandedHandler()}
          aria-label={row.getIsExpanded() ? "Collapse scan logs" : "Expand scan logs"}
        >
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: row.getIsExpanded() ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s ease",
            }}
          >
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      );
    },
  }),
  columnHelper.accessor("probillNumber", {
    id: "probillNumber",
    header: "Probill",
    size: 110,
    cell: ({ getValue }) => (
      <Link
        className="xgs-table-link-cell"
        to={`/shipments/${getValue()}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {getValue()}
      </Link>
    ),
    filterFn: "includesString",
  }),
  columnHelper.accessor((row) => row.status?.title ?? "", {
    id: "status",
    header: "Status",
    size: 140,
    cell: ({ row }) => {
      const title = row.original.status?.title;
      if (!title) return "—";
      return (
        <Tag mods={{ color: getStatusColor(title), uppercase: true }}>{title}</Tag>
      );
    },
    meta: { filterVariant: "select" },
    filterFn: "equalsString",
  }),
  columnHelper.accessor((row) => formatManifestShipmentTerminal(row.origin), {
    id: "origin",
    header: "Origin",
    size: 180,
    cell: ({ getValue }) => getValue() || "—",
    meta: { filterVariant: "select" },
    filterFn: "equalsString",
  }),
  columnHelper.accessor((row) => formatManifestShipmentTerminal(row.destination), {
    id: "destination",
    header: "Destination",
    size: 180,
    cell: ({ getValue }) => getValue() || "—",
    meta: { filterVariant: "select" },
    filterFn: "equalsString",
  }),
  columnHelper.accessor("etaDate", {
    id: "etaDate",
    header: "ETA",
    size: 100,
    cell: ({ getValue }) => formatDate(getValue()),
    meta: {
      filterVariant: "select",
      filterLabelFormatter: (key: string | number | undefined | null) =>
        formatDate(key == null ? null : String(key)),
    },
    filterFn: "equals",
  }),
  columnHelper.accessor("rollCount", {
    id: "rollCount",
    header: "Rolls",
    size: 80,
    cell: ({ getValue }) => formatNumber(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("palletCount", {
    id: "palletCount",
    header: "Pallets",
    size: 80,
    cell: ({ getValue }) => formatNumber(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("huCount", {
    id: "huCount",
    header: "HU",
    size: 70,
    cell: ({ getValue }) => formatNumber(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("totalWeight", {
    id: "totalWeight",
    header: "Weight",
    size: 100,
    cell: ({ getValue }) => {
      const v = getValue();
      if (v == null) return "—";
      return `${formatNumber(v)} lbs`;
    },
    filterFn: "includesString",
  }),
  columnHelper.accessor("poNumber", {
    id: "poNumber",
    header: "PO",
    size: 110,
    cell: ({ getValue }) => getValue() || "—",
    filterFn: "includesString",
  }),
  columnHelper.accessor("bolNumber", {
    id: "bolNumber",
    header: "BOL",
    size: 110,
    cell: ({ getValue }) => getValue() || "—",
    filterFn: "includesString",
  }),
  columnHelper.accessor("billDate", {
    id: "billDate",
    header: "Bill Date",
    size: 100,
    cell: ({ getValue }) => formatDate(getValue()),
    meta: {
      filterVariant: "select",
      filterLabelFormatter: (key: string | number | undefined | null) =>
        formatDate(key == null ? null : String(key)),
    },
    filterFn: "equals",
  }),
  columnHelper.accessor("rate", {
    id: "rate",
    header: "Rate",
    size: 100,
    cell: ({ getValue }) => formatCurrency(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor((row) => row.pod?.date ?? "", {
    id: "podDate",
    header: "POD Date",
    size: 100,
    cell: ({ row }) => formatDate(row.original.pod?.date),
    meta: {
      filterVariant: "select",
      filterLabelFormatter: (key: string | number | undefined | null) =>
        formatDate(key == null || key === "" ? null : String(key)),
    },
    filterFn: "equalsString",
  }),
];

export const getRowCanExpandManifestShipment = (
  _row: Row<LoadboardManifestShipment>
): boolean => true;
